import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    DataTableCustomized,
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    activateRegulation,
    deleteRegulation,
    getRegulationsList,
    getRegulationsStats,
} from '@/modules/Regulations/services'
import { RegulationsConfig } from '@/modules/Regulations/types'
import { Attachment } from '@/modules/Upload/types'
import {
    CheckCircle,
    Eye,
    Pencil,
    Play,
    ScrollText,
    Trash2,
    Users,
} from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateEditRegulationModalProps } from './CreateEditModal'

const RegulationManagement: React.FC = () => {
    const { t } = useTranslation()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { openFilePreview } = useFilePreviewWithPresigned()

    const handlePreviewAttachment = useCallback(
        (attachment: Attachment) => {
            openFilePreview(attachment)
        },
        [openFilePreview]
    )

    const {
        data: regulations = [],
        isFetching,
        renderState,
        refetch: refetchRegulations,
    } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.REGULATIONS_LIST],
            queryFn: () => getRegulationsList(),
        },
        {
            showLoading: true,
            showError: true,
        }
    )

    const { data: stats, refetch: refetchStats } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REGULATIONS_STATS],
        queryFn: getRegulationsStats,
    })

    const { mutateAsync: handleDeleteRegulation } = useMutationWithHandler(
        {
            mutationFn: deleteRegulation,
        },
        {
            successMessage: t('regulations:regulations-deleted-success'),
            reactQueryKeys: [QUERY_KEYS.REGULATIONS_LIST],
        }
    )

    const { mutateAsync: handleActivateRegulation } = useMutationWithHandler(
        {
            mutationFn: activateRegulation,
        },
        {
            successMessage: t('regulations:regulations-activated-success'),
            reactQueryKeys: [
                QUERY_KEYS.REGULATIONS_LIST,
                QUERY_KEYS.REGULATIONS_STATS,
            ],
        }
    )

    const handleEdit = (regulation: RegulationsConfig) => {
        setModal<CreateEditRegulationModalProps>(
            'RegulationModals/CreateEdit/index',
            {
                regulation,
            }
        )
    }

    const handleDelete = (regulation: RegulationsConfig) => {
        setConfirmation({
            open: true,
            title: t('regulations:delete-regulation'),
            description: t(
                'regulations:confirm-delete-regulation-description',
                {
                    version: regulation.regulation_version,
                }
            ),
            onConfirm: () => handleDeleteRegulation(regulation.regulation_id),
        })
    }

    const handleActivate = (regulation: RegulationsConfig) => {
        setConfirmation({
            open: true,
            title: t('regulations:activate-regulation'),
            description: t(
                'regulations:confirm-activate-regulation-description',
                {
                    version: regulation.regulation_version,
                }
            ),
            onConfirm: async () => {
                await handleActivateRegulation(regulation.regulation_id)
                await Promise.all([refetchRegulations(), refetchStats()])
            },
        })
    }

    const columns = useMemo(
        (): DataTableColumn<RegulationsConfig>[] => [
            {
                key: 'regulation_version',
                title: t('version'),
                width: 100,
                cellClassName: 'truncate',
                formatter: {
                    type: 'chip',
                    colorMap: (value) => ({
                        label: `v${String(value).replace(/^v/i, '')}`,
                        color: 'default',
                        className: 'font-mono',
                    }),
                },
            },
            {
                key: 'regulation_title',
                title: t('title'),
                width: 280,
                cellClassName: 'truncate',
                render: (_value, row) => (
                    <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">
                            {row.regulation_title}
                        </span>
                        {row.regulation_summary && (
                            <span className="text-xs text-muted-foreground truncate">
                                {row.regulation_summary}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                key: 'attachments',
                title: t('attachment'),
                width: 180,
                cellClassName: 'truncate',
                render: (_value, row) => {
                    if (!row.attachments || row.attachments.length === 0) {
                        return (
                            <span className="text-muted-foreground text-xs">
                                -
                            </span>
                        )
                    }
                    const att = row.attachments[0]
                    return (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-xs h-7 px-2"
                            onClick={() => handlePreviewAttachment(att)}
                        >
                            <Eye className="h-3.5 w-3.5" />
                            <span className="max-w-[100px] truncate">
                                {att.attachment_file_name}
                            </span>
                        </Button>
                    )
                },
            },
            {
                key: 'regulation_type',
                title: t('type'),
                width: 120,
                cellClassName: 'truncate',
                formatter: {
                    type: 'chip',
                    colorMap: (value) => ({
                        label: value,
                        color: 'primary',
                    }),
                },
            },
            {
                key: 'effective_date',
                title: t('effective-date'),
                width: 130,
                cellClassName: 'truncate',
                formatter: {
                    type: 'date',
                    format: 'DD/MM/YYYY',
                },
            },
            {
                key: 'is_mandatory',
                title: t('mandatory'),
                width: 110,
                cellClassName: 'text-center truncate',
                formatter: {
                    type: 'options',
                    options: [
                        {
                            value: true,
                            label: t('required'),
                            color: 'error',
                        },
                        {
                            value: false,
                            label: t('optional'),
                            color: 'secondary',
                        },
                    ],
                    size: 'sm',
                },
            },
            {
                key: 'is_active',
                title: t('status'),
                width: 100,
                cellClassName: 'text-center truncate',
                formatter: {
                    type: 'options',
                    options: [
                        {
                            value: true,
                            label: t('active'),
                            color: 'success',
                        },
                        {
                            value: false,
                            label: t('inactive'),
                            color: 'secondary',
                        },
                    ],
                    showDot: true,
                    variant: 'dot',
                },
            },
        ],
        [t, handlePreviewAttachment]
    )

    const actions = useMemo(
        (): DataTableAction<RegulationsConfig>[] => [
            {
                key: 'activate',
                label: t('regulations:activate-regulation'),
                icon: <Play className="h-4 w-4" />,
                variant: 'success',
                onClick: (row) => handleActivate(row),
                hidden: (row) => row.is_active,
            },
            {
                key: 'edit',
                label: t('edit'),
                icon: <Pencil className="h-4 w-4" />,
                onClick: (row) => handleEdit(row),
            },
            {
                key: 'delete',
                label: t('delete'),
                icon: <Trash2 className="h-4 w-4" />,
                variant: 'danger',
                onClick: (row) => handleDelete(row),
                disabled: (row) => row.is_active,
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Users className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('total-employees')}
                                    </p>
                                    <p className="text-2xl font-bold">
                                        {stats.total_active_employees}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('accepted')}
                                    </p>
                                    <p className="text-2xl font-bold text-green-600">
                                        {stats.accepted_count}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-amber-100 rounded-lg">
                                    <Users className="h-5 w-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('not-accepted')}
                                    </p>
                                    <p className="text-2xl font-bold text-amber-600">
                                        {stats.not_accepted_count}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <ScrollText className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('acceptance-rate')}
                                    </p>
                                    <p className="text-2xl font-bold text-purple-600">
                                        {stats.acceptance_rate.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Paper>
                <DataTableCustomized
                    columns={columns}
                    rows={regulations}
                    rowKey={(row) => row.regulation_id}
                    size="small"
                    resizable
                    isFetching={isFetching}
                    loading={!regulations.length && isFetching}
                    emptyMessage={t('regulations:no-regulations-found')}
                    actions={actions}
                />
            </Paper>
        </div>
    )
}

export default RegulationManagement
